import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../../lib/api";
import { SuperCard } from "../../components/SuperAdminLayout";
import { useAuth } from "../../lib/auth";
import { notify } from "../../lib/notify";
import { confirmDialog } from "../../components/ConfirmDialog";
import { Eye, Percent, Tag, Ban, CheckCircle2, ArrowLeft } from "lucide-react";

export default function SuperAttractionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [d, setD] = useState(null);
  const [rateInput, setRateInput] = useState("");
  const [promo, setPromo] = useState({ type: "volume", ticket_limit: 100, duration_days: 30 });

  const load = () => api.get(`/super/attractions/${id}`).then((r) => { setD(r.data); setRateInput(r.data.rate.rate_source === "custom" ? (r.data.attraction.commission_rate * 100).toString() : ""); });
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);
  if (!d) return <div className="text-[#8a8a8a] py-20 text-center">Loading…</div>;
  const a = d.attraction;

  const setCommission = async (rate) => {
    try { await api.patch(`/super/attractions/${id}/commission`, { commission_rate: rate }); notify.success("Commission updated"); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail)); }
  };
  const saveRate = () => {
    const v = parseFloat(rateInput);
    if (isNaN(v) || v < 0 || v > 100) return notify.error("Enter a rate between 0 and 100");
    setCommission(v / 100);
  };
  const createPromo = async () => {
    try {
      const body = { type: promo.type };
      if (promo.type !== "duration") body.ticket_limit = Number(promo.ticket_limit);
      if (promo.type !== "volume") body.duration_days = Number(promo.duration_days);
      await api.post(`/super/attractions/${id}/promo`, body); notify.success("Promo activated (0% commission)"); load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail)); }
  };
  const removePromo = async () => {
    if (!(await confirmDialog({ title: "Remove promo?", description: "Commission reverts to the attraction's rate.", confirmLabel: "Remove", destructive: true }))) return;
    await api.delete(`/super/attractions/${id}/promo`); notify.success("Promo removed"); load();
  };
  const setStatus = async (status) => {
    const labels = { active: "Reactivate", suspended: "Suspend", banned: "Ban" };
    if (status !== "active" && !(await confirmDialog({ title: `${labels[status]} ${a.name}?`, description: status === "banned" ? "Banning hides the booking page permanently." : "Suspending hides the public booking page until reactivated.", confirmLabel: labels[status], destructive: true }))) return;
    await api.post(`/super/attractions/${id}/status`, { status }); notify.success(`${labels[status]}d`); load();
  };
  const impersonate = async () => {
    await api.post(`/super/impersonate/${id}`);
    await refresh();
    nav("/dashboard/overview");
  };

  return (
    <div className="space-y-6" data-testid="super-attraction-detail">
      <Link to="/super-admin/attractions" className="text-sm text-[#8a8a8a] hover:text-white flex items-center gap-1.5"><ArrowLeft size={14} /> All attractions</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{a.name}</h1>
          <div className="text-sm text-[#8a8a8a]">/{a.slug} · {d.owner_email || "no owner"} · {a.stripe_onboarding_complete ? "Stripe connected" : "No Stripe"} · status: {a.status || "active"}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={impersonate} className="st-btn" data-testid="impersonate-btn"><Eye size={14} /> View as attraction</button>
          {(a.status || "active") === "active" ? (
            <>
              <button onClick={() => setStatus("suspended")} className="st-btn st-btn-ghost text-[#facc15]" data-testid="suspend-btn"><Ban size={14} /> Suspend</button>
              <button onClick={() => setStatus("banned")} className="st-btn st-btn-ghost text-[#fca5a5]" data-testid="ban-btn">Ban</button>
            </>
          ) : (
            <button onClick={() => setStatus("active")} className="st-btn st-btn-ghost text-[#86efac]" data-testid="reactivate-btn"><CheckCircle2 size={14} /> Reactivate</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Tickets sold" value={d.stats.tickets_sold} />
        <Stat label="Gross sales" value={poundsFromPence(d.stats.gross_pence)} />
        <Stat label="Commission earned" value={poundsFromPence(d.stats.commission_pence)} />
        <Stat label="Orders" value={d.stats.orders_count} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SuperCard className="p-5">
          <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4"><Percent size={14} /> Commission rate</h3>
          <div className="text-sm text-[#bbb] mb-3">Currently <span className="font-bold text-white">{(d.rate.effective_rate * 100).toFixed(2)}%</span> <span className="text-[#facc15]">({d.rate.rate_source})</span></div>
          <div className="flex items-center gap-2">
            <input className="st-input w-28" type="number" step="0.1" min="0" max="100" placeholder="e.g. 3" value={rateInput} onChange={(e) => setRateInput(e.target.value)} data-testid="commission-input" /><span>%</span>
            <button className="st-btn" onClick={saveRate} data-testid="commission-save">Set custom</button>
            <button className="st-btn st-btn-ghost text-sm" onClick={() => setCommission(null)} data-testid="commission-reset">Use platform default</button>
          </div>
        </SuperCard>

        <SuperCard className="p-5">
          <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4"><Tag size={14} /> Promotional 0% commission</h3>
          {d.rate.promo ? (
            <div className="text-sm" data-testid="active-promo">
              <div className="text-[#86efac] font-medium">Active {d.rate.promo.type} promo</div>
              <div className="text-[#bbb] mt-1">
                {d.rate.promo.ticket_limit ? `${d.rate.promo.tickets_used}/${d.rate.promo.ticket_limit} tickets used. ` : ""}
                {d.rate.promo.end_date ? `Ends ${String(d.rate.promo.end_date).slice(0, 10)}.` : ""}
              </div>
              <button className="st-btn st-btn-ghost text-[#fca5a5] text-sm mt-3" onClick={removePromo} data-testid="promo-remove">Remove promo</button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="promo-create">
              <select className="st-input" value={promo.type} onChange={(e) => setPromo({ ...promo, type: e.target.value })} data-testid="promo-type">
                <option value="volume">First N tickets free of commission</option>
                <option value="duration">Free commission for N days</option>
                <option value="hybrid">Whichever comes first (tickets or days)</option>
              </select>
              {promo.type !== "duration" && <label className="block text-sm text-[#bbb]">Ticket limit<input className="st-input mt-1" type="number" min="1" value={promo.ticket_limit} onChange={(e) => setPromo({ ...promo, ticket_limit: e.target.value })} data-testid="promo-tickets" /></label>}
              {promo.type !== "volume" && <label className="block text-sm text-[#bbb]">Duration (days)<input className="st-input mt-1" type="number" min="1" value={promo.duration_days} onChange={(e) => setPromo({ ...promo, duration_days: e.target.value })} data-testid="promo-days" /></label>}
              <button className="st-btn w-full justify-center" onClick={createPromo} data-testid="promo-create-btn">Activate 0% promo</button>
            </div>
          )}
        </SuperCard>
      </div>

      <SuperCard className="p-5" data-testid="setup-status">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Setup status {d.checklist?.ready_to_sell && <span className="text-[#86efac] normal-case tracking-normal">· ready to sell ✓</span>}</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {d.checklist?.items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 text-sm">
              <span className={it.complete ? "text-[#86efac]" : "text-[#5a5a5a]"}>{it.complete ? "✓" : "○"}</span>
              <span className={it.complete ? "text-[#8a8a8a]" : ""}>{it.label}{it.required && !it.complete && <span className="text-[10px] text-[#dc2626] ml-1">required</span>}</span>
            </div>
          ))}
        </div>
      </SuperCard>

      <SuperCard className="p-5">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Recent orders</h3>
        <div className="space-y-1 text-sm">
          {d.recent_orders.length === 0 && <div className="text-[#7a7a7a]">No orders yet.</div>}
          {d.recent_orders.map((o) => (
            <div key={o.id} className="flex justify-between py-1.5 border-b border-[#161616] last:border-0">
              <span className="st-mono text-[#dc2626]">#{o.order_number}</span>
              <span className="text-[#bbb]">{o.customer_name}</span>
              <span>{poundsFromPence(o.gross_amount)}</span>
              <span className="text-[#7a7a7a] text-xs">{String(o.created_at).slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </SuperCard>
    </div>
  );
}

function Stat({ label, value }) {
  return <SuperCard className="p-4"><div className="text-2xl font-bold">{value}</div><div className="text-xs text-[#8a8a8a]">{label}</div></SuperCard>;
}
